import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@mui/icons-material';

// Canonical default page size for every table in the Bundle tool. Callers may omit `rowsPerPage`
// to inherit this value.
export const DEFAULT_PAGE_SIZE = 25;

// Comfortable 44px tap targets on phones (warehouse devices), compact on desktop.
const pagerBtnSx = {
    width: { xs: 44, sm: 34 },
    height: { xs: 44, sm: 34 },
} as const;

// Shared pager for every table in the Bundle tool. `page` is 0-indexed.
const TablePaginationBar: React.FC<{
    page: number;
    total: number;
    rowsPerPage?: number;
    onPageChange: (page: number) => void;
}> = ({ page, total, rowsPerPage = DEFAULT_PAGE_SIZE, onPageChange }) => {
    if (total <= rowsPerPage) return null;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, total)} of {total}
                </Typography>
                <IconButton size="small" aria-label="First page" onClick={() => onPageChange(0)} disabled={page === 0} sx={pagerBtnSx}>
                    <FirstPage fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Previous page" onClick={() => onPageChange(page - 1)} disabled={page === 0} sx={pagerBtnSx}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'center' }}>
                    Page {page + 1} of {Math.max(1, Math.ceil(total / rowsPerPage))}
                </Typography>
                <IconButton size="small" aria-label="Next page" onClick={() => onPageChange(page + 1)} disabled={(page + 1) * rowsPerPage >= total} sx={pagerBtnSx}>
                    <ChevronRight fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Last page" onClick={() => onPageChange(Math.max(0, Math.ceil(total / rowsPerPage) - 1))} disabled={(page + 1) * rowsPerPage >= total} sx={pagerBtnSx}>
                    <LastPage fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

export default TablePaginationBar;
